import React, { useState, useEffect } from 'react';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function ClientDetailView({ client, onBack, cases, documents, dbOps, onOpenNewCase, onViewDoc, storage, currentUserProfile }) {
  const clientCases = cases.filter(c => c.client === client.name);
  const clientDocs = documents.filter(d => d.clientId === client._id);
  
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(false);
  
  const [feeAmount, setFeeAmount] = useState(client.totalFee || 0);
  const [invoiceData, setInvoiceData] = useState({ amount: '', method: 'העברה בנקאית', refNumber: '', hasVat: true });

  const totalFee = client.totalFee || 0;
  const paid = client.paid || 0;
  const balance = totalFee - paid;
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [localPhotoUrl, setLocalPhotoUrl] = useState(client.photoUrl);

  useEffect(() => {
    setLocalPhotoUrl(client.photoUrl);
  }, [client.photoUrl]);

  const handleDirectPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !storage) return;
    
    try {
      setIsUploadingPhoto(true);
      const photoRef = ref(storage, `client-photos/${client.idNumber || Date.now()}_manual.jpg`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);
      dbOps.handleSave('clients', { photoUrl: url }, client._id);
      setLocalPhotoUrl(url);
    } catch (err) {
      console.error("Failed to upload photo", err);
      alert('שגיאה בהעלאת התמונה');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSaveFee = () => {
    dbOps.handleSave('clients', { totalFee: Number(feeAmount) }, client._id);
    setEditingFee(false);
  };

  const handleGenerateInvoice = (e) => {
    e.preventDefault();
    const val = Number(invoiceData.amount);
    if (isNaN(val) || val <= 0) return;

    dbOps.handleSave('clients', { paid: paid + val }, client._id);
    
    const dateStr = new Date().toISOString().split('T')[0];
    const docName = `חשבונית_מס_קבלה_${client.name.replace(/ /g, '_')}_${dateStr}.pdf`;
    dbOps.handleSave('documents', {
      name: docName,
      clientId: client._id,
      uploadedBy: currentUserProfile?.name || 'מערכת הנה"ח',
      date: dateStr,
      size: '0.2 MB',
      securityLevel: 'רגיל',
      type: 'invoice',
      invoiceDetails: invoiceData
    });

    setInvoiceModalOpen(false);
    setInvoiceData({ amount: '', method: 'העברה בנקאית', refNumber: '', hasVat: true });
    alert('החשבונית הופקה בהצלחה ונשמרה במסמכי הלקוח.');
  };

  const generateLedger = () => {
    alert(`כרטסת הנהלת חשבונות ללקוח ${client.name} מופקת כעת (הדמיה)...`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300 relative pb-10">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-medium transition-colors mb-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200 w-fit"><ArrowRight className="w-4 h-4" /> חזרה לרשימת הלקוחות</button>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6 flex flex-col md:flex-row items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <div className="relative group">
              {localPhotoUrl ? (
                <img src={localPhotoUrl} alt={client.name} className={`w-16 h-16 rounded-full object-cover border-2 border-indigo-100 shadow-sm shrink-0 ${isUploadingPhoto ? 'opacity-50' : ''}`} />
              ) : (
                <div className={`w-16 h-16 rounded-full bg-indigo-50 border-2 border-indigo-100 flex items-center justify-center shrink-0 ${isUploadingPhoto ? 'opacity-50' : ''}`}>
                  <Users className="w-8 h-8 text-indigo-700" />
                </div>
              )}
              {isUploadingPhoto && <RefreshCw className="absolute inset-0 m-auto w-6 h-6 text-blue-600 animate-spin" />}
              <label className="absolute -bottom-1 -right-1 bg-white p-1.5 rounded-full shadow border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" title="שנה תמונת לקוח">
                <Edit className="w-3.5 h-3.5 text-slate-600" />
                <input type="file" className="hidden" accept="image/*" onChange={handleDirectPhotoUpload} />
              </label>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800">{client.name}</h2>
          </div>
          <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-slate-600 mt-4">
            <p><strong>ת.ז / ח.פ:</strong> {client.idNumber}</p><p><strong>טלפון:</strong> <span dir="ltr">{client.phone}</span></p><p className="break-all"><strong>דוא"ל:</strong> {client.email}</p>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-4 md:gap-6 w-full md:w-auto overflow-x-auto">
          <div className="text-center min-w-[80px]">
            <p className="text-xs text-slate-500 mb-1 flex items-center justify-center gap-1">שכ"ט כולל 
              <button onClick={() => setEditingFee(!editingFee)} className="text-blue-500 hover:text-blue-700"><Edit className="w-3 h-3"/></button>
            </p>
            {editingFee ? (
               <div className="flex items-center gap-1">
                 <input type="number" value={feeAmount} onChange={e=>setFeeAmount(e.target.value)} className="w-20 p-1 text-sm border border-blue-300 rounded outline-none" />
                 <button onClick={handleSaveFee} className="bg-blue-600 text-white p-1 rounded"><CheckCircle2 className="w-4 h-4"/></button>
               </div>
            ) : (
              <p className="font-bold text-slate-800">₪{totalFee.toLocaleString()}</p>
            )}
          </div>
          <div className="w-px bg-slate-200 shrink-0"></div>
          <div className="text-center min-w-[80px]"><p className="text-xs text-slate-500 mb-1">שולם</p><p className="font-bold text-emerald-600">₪{paid.toLocaleString()}</p></div>
          <div className="w-px bg-slate-200 shrink-0"></div>
          <div className="text-center min-w-[80px]"><p className="text-xs text-slate-500 mb-1">יתרה</p><p className={`font-bold ${balance > 0 ? 'text-red-600' : 'text-slate-800'}`}>₪{balance.toLocaleString()}</p></div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><Banknote className="w-5 h-5 text-emerald-600" /> הנהלת חשבונות</h3>
          <button onClick={() => setInvoiceModalOpen(true)} className="w-full bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"><Receipt className="w-4 h-4" /> הפקת חשבונית מס / קבלה</button>
          <button onClick={generateLedger} className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"><FileText className="w-4 h-4" /> הפקת כרטסת הנה"ח ללקוח</button>
        </div>
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><Briefcase className="w-5 h-5 text-blue-600" /> הליכים ותיקים משפטיים</h3>
            <button onClick={() => onOpenNewCase(client.name)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"><Plus className="w-3.5 h-3.5" /> תיק חדש ללקוח</button>
          </div>
          <div className="p-4 overflow-auto max-h-[300px]">
            {clientCases.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">אין תיקים ללקוח זה</p> : 
              <div className="space-y-3">
                {clientCases.map(c => (
                  <div key={c._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-slate-100 rounded-xl bg-slate-50/50 hover:bg-white hover:shadow-sm gap-2">
                    <div>
                      <p className="font-medium text-blue-700 text-sm mb-1">{c.id} - {c.title}</p>
                      <p className="text-xs text-slate-500">סוג: {c.type} | עו"ד מטפל: {c.lawyer}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium border w-fit ${c.status === 'פעיל' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.status}</span>
                      <a href={`?caseId=${c._id}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 p-1" title="פתח תיק בכרטיסייה חדשה"><ExternalLink className="w-4 h-4"/></a>
                    </div>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>

        {/* Client Documents Section */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col mt-2">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" /> מסמכי הנהלת חשבונות ולקוח</h3>
          </div>
          <div className="p-4 overflow-auto max-h-[300px]">
            {clientDocs.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">טרם הופקו מסמכים ללקוח זה.</p> : 
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clientDocs.map(doc => (
                  <div key={doc._id} className="flex items-center gap-3 border border-slate-100 p-3 rounded-xl bg-slate-50/50 hover:bg-blue-50 cursor-pointer transition-colors group" onClick={() => onViewDoc && onViewDoc(doc)}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600 shrink-0"><FileText className="w-5 h-5" /></div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-800 truncate group-hover:text-blue-700" title={`${doc.name} (לחיצה כפולה להורדה)`} onDoubleClick={(e) => handleDownloadLocalFile(doc, e)}>{doc.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{formatHebDate(doc.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>

      </div>

      {/* Invoice Modal */}
      {invoiceModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600"/> הפקת חשבונית מס / קבלה</h3>
              <button onClick={() => setInvoiceModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleGenerateInvoice} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">סכום הקבלה (₪) <span className="text-red-500">*</span></label>
                <input required type="number" min="1" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={invoiceData.amount} onChange={e => setInvoiceData({...invoiceData, amount: e.target.value})} placeholder="0" />
              </div>
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                <input type="checkbox" id="vat" checked={invoiceData.hasVat} onChange={(e) => setInvoiceData({...invoiceData, hasVat: e.target.checked})} className="w-4 h-4 text-blue-600" />
                <label htmlFor="vat" className="text-sm text-slate-700 cursor-pointer">הסכום כולל מע"מ</label>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">אמצעי תשלום <span className="text-red-500">*</span></label>
                <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:border-blue-500" value={invoiceData.method} onChange={e => setInvoiceData({...invoiceData, method: e.target.value})}>
                  <option value="העברה בנקאית">העברה בנקאית</option>
                  <option value="כרטיס אשראי">כרטיס אשראי</option>
                  <option value="המחאה (צ'ק)">המחאה (צ'ק)</option>
                  <option value="מזומן">מזומן</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">מספר אסמכתא / פרטי העברה (צ'ק/בנק)</label>
                <input type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={invoiceData.refNumber} onChange={e => setInvoiceData({...invoiceData, refNumber: e.target.value})} placeholder="למשל: בנק 12, סניף 345..." />
              </div>
              
              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-4">
                <button type="button" onClick={() => setInvoiceModalOpen(false)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">ביטול</button>
                <button type="submit" className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors">הפק ושלח ללקוח</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
